import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * GN — omaggi a scaglioni: pulsante flottante + pannello (sezione gn-gift-fab).
 *
 * Le quantità da impostare le calcola già il Liquid (data-updates); qui si applicano con /cart/update,
 * come in gn-cart-protection. Quantità assolute per variante: ripetere la stessa richiesta non fa danni.
 * Un omaggio trovato a pagamento viene tolto e non più riaggiunto in questa sessione, altrimenti con
 * uno sconto scaduto si entrerebbe in un ciclo aggiungi/togli.
 * A ogni cambio carrello la sezione si ricarica (Section Rendering API) e il ciclo riparte.
 */
const SECTION = 'gn-gift-fab';
// v2: azzera i blocchi salvati dalla versione che bloccava anche gli omaggi tolti sotto soglia
const KEY = 'gn-gifts-blocked-v2';
let running = false;
let wanted = null;

function readBlocked() {
  try {
    return JSON.parse(sessionStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
}

async function sync() {
  if (running) return;
  running = true;
  try {
    while (wanted !== null) {
      const updates = wanted;
      wanted = null;
      await apply(updates);
    }
  } finally {
    running = false;
  }
}

async function apply(updates) {
  const components = [...document.querySelectorAll('cart-items-component[data-section-id]')];
  const sections = [...new Set(components.map((c) => c.dataset.sectionId).filter(Boolean))];

  const promise = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'change',
      context: 'cart',
      lines: Object.entries(updates).map(([id, quantity]) => ({ merchandiseId: id, quantity })),
      promise: promise.promise,
      detail: { source: 'gn-gift-fab' },
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) throw new Error(result.description || result.message || 'Cart update failed');
    promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
      detail: { sections: result.sections, source: 'gn-gift-fab', didError: false },
    });
  } catch (error) {
    promise.reject(error);
    document.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID', detail: { source: 'gn-gift-fab' } }));
  }
}

class GnGiftFab extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-gn-fab-toggle]');
    this.panel = this.querySelector('.gn-fab__panel');
    this.button.addEventListener('click', this.toggle);
    this.querySelector('[data-gn-fab-close]').addEventListener('click', this.close);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onOutsideClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.syncCart();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  toggle = () => (this.panel.hidden ? this.open() : this.close());

  open = () => {
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.querySelector('[data-gn-fab-close]').focus();
  };

  close = () => {
    if (this.panel.hidden) return;
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    this.button.focus();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  onOutsideClick = (event) => {
    if (!this.panel.hidden && !this.contains(event.target)) {
      this.panel.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
    }
  };

  /** Applica le quantità calcolate dal Liquid, saltando gli omaggi trovati a pagamento. */
  syncCart() {
    let blocked = readBlocked();
    const paid = (this.dataset.paid || '').split(',').filter(Boolean);
    if (paid.length) {
      blocked = [...new Set([...blocked, ...paid])];
      try {
        sessionStorage.setItem(KEY, JSON.stringify(blocked));
      } catch {}
    }

    const updates = JSON.parse(this.dataset.updates || '{}');
    for (const id in updates) if (updates[id] > 0 && blocked.includes(id)) delete updates[id];
    if (!Object.keys(updates).length) return;
    wanted = updates;
    sync();
  }

  onCartUpdate = (event) => {
    event.promise
      ?.then(() => this.refresh())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[gn-gift-fab] Event promise rejected:', error);
      });
  };

  /** Ricarica la sezione e ne copia stato e contenuto, lasciando il pannello aperto o chiuso com'era. */
  async refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=${SECTION}`);
      const html = (await response.json())[SECTION];
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('gn-gift-fab');
      if (!fresh) {
        this.hidden = true;
        return;
      }
      this.hidden = fresh.hidden;
      this.className = fresh.className;
      this.dataset.updates = fresh.dataset.updates;
      this.dataset.paid = fresh.dataset.paid;
      this.button.setAttribute('aria-label', fresh.querySelector('[data-gn-fab-toggle]').getAttribute('aria-label'));
      this.querySelector('[data-gn-fab-body]').replaceWith(fresh.querySelector('[data-gn-fab-body]'));
      this.syncCart();
    } catch (error) {
      console.warn('[gn-gift-fab] Aggiornamento non riuscito:', error);
    }
  }
}

if (!customElements.get('gn-gift-fab')) customElements.define('gn-gift-fab', GnGiftFab);
